<template>
  <div class="vorg">
    <APageHead
      title="바우처 · 기관 관리"
      sub="바우처를 미리 사 두는 기관(학교)과 잔액을 관리합니다. 기관은 삭제하지 않고 비활성화합니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="reloadAll">새로고침</ABtn>
        <ABtn kind="primary" icon="add" @click="formOpen = true">기관 등록</ABtn>
      </template>
    </APageHead>

    <!-- 전체 요약 (GET /voucher/admin/balance/overview) -->
    <div class="vorg__stats">
      <div v-for="c in overview.byCurrency" :key="c.currency" class="vorg__stat">
        <AStat :label="`잔액 합계 · ${c.currency} · ${c.orgCount}곳`" :value="money(c.balance, c.currency)">
          <div class="vorg__stat-sub">
            이번 달 적립 {{ money(c.monthPurchase, c.currency) }} · 사용 {{ money(c.monthUse, c.currency) }}
          </div>
        </AStat>
      </div>
      <div v-if="overview.usdEquivalent != null" class="vorg__stat">
        <AStat label="USD 환산 합계 (참고치)" :value="money(overview.usdEquivalent, 'USD')">
          <div class="vorg__stat-sub">통화별 잔액을 당일 환율로 더한 값입니다{{ overview.asOf ? ` · ${fmtKst(overview.asOf)} 기준` : "" }}</div>
        </AStat>
      </div>
      <button type="button" class="vorg__stat vorg__stat--btn" @click="$router.push({ name: 'VoucherPurchases' })">
        <AStat label="입금 대기 구매 요청" :value="overview.requestedCount" unit="건">
          <div class="vorg__stat-sub">
            <template v-if="overview.requestedByCurrency.length">
              {{ overview.requestedByCurrency.map((r) => money(r.amount, r.currency)).join(" · ") }} ·
            </template>
            구매 확정으로 이동
          </div>
        </AStat>
      </button>
      <button type="button" class="vorg__stat vorg__stat--btn" @click="filterShort">
        <AStat label="잔액 부족 예상 기관" :value="overview.shortCount" unit="곳">
          <div class="vorg__stat-sub" :class="{ 'vorg__stat-sub--err': overview.shortCount > 0 }">
            {{ overview.shortCount > 0 ? '등록 대기 예상액보다 잔액이 적습니다' : '없음' }}
          </div>
        </AStat>
      </button>
    </div>

    <ACard overflow="visible">
      <div class="vorg__filters">
        <div class="p-field vorg__filter--wide">
          <label class="p-field__label">검색어</label>
          <input v-model.trim="filters.q" class="p-input" type="text" placeholder="기관명 · 영문명 · 담당자" @keyup.enter="search" />
        </div>
        <div class="p-field">
          <label class="p-field__label">상태</label>
          <select v-model="filters.status" class="p-input" @change="search">
            <option value="">전체</option>
            <option value="Y">활성</option>
            <option value="N">비활성</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">바우처 통화</label>
          <select v-model="filters.currency" class="p-input" @change="search">
            <option value="">전체</option>
            <option v-for="c in currencyFilterOptions" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">잔액</label>
          <select v-model="filters.balance_filter" class="p-input" @change="search">
            <option value="">전체</option>
            <option value="short">부족 예상</option>
            <option value="zero">0</option>
            <option value="positive">있음</option>
          </select>
        </div>
        <div class="vorg__filter-actions">
          <ABtn kind="primary" icon="search" @click="search">조회</ABtn>
          <ABtn kind="ghost" icon="refresh" @click="resetFilters">초기화</ABtn>
        </div>
      </div>
    </ACard>

    <ACard>
      <template #action>
        <APill kind="info">{{ total.toLocaleString() }}곳</APill>
      </template>
      <div v-if="loading" class="vorg__loading"><div class="p-spinner p-spinner--lg"></div></div>
      <LoadError v-else-if="loadError" :message="loadError" @retry="load" />
      <AEmpty v-else-if="!items.length" title="기관이 없습니다" sub="오른쪽 위 「기관 등록」으로 추가해 주세요." />
      <template v-else>
        <ATable :columns="columns" :rows="items" row-key="organization_idx">
          <template #cell:name="{ row }">
            <div class="vorg__stack">
              <router-link class="vorg__link" :to="detailRoute(row)">{{ row.name }}</router-link>
              <span v-if="row.name_en" class="vorg__sub">{{ row.name_en }}</span>
            </div>
          </template>
          <template #cell:status="{ row }">
            <APill :kind="row.active_yn === 'N' ? 'default' : 'ok'" dot>
              {{ row.active_yn === 'N' ? '비활성' : '활성' }}
            </APill>
          </template>
          <template #cell:currency="{ row }">
            <div class="vorg__stack">
              <span class="vorg__mono">{{ row.voucher_currency }}</span>
              <span class="vorg__sub">할인 {{ percent(row.voucher_discount_rate) }}</span>
            </div>
          </template>
          <template #cell:balance="{ row }">
            <div class="vorg__stack vorg__stack--end">
              <b class="vorg__mono">{{ money(row.voucher_balance, row.voucher_currency) }}</b>
              <span v-if="isPositive(row.pending_estimate)" class="vorg__sub vorg__mono">
                대기 예상 {{ money(row.pending_estimate, row.voucher_currency) }}
              </span>
            </div>
          </template>
          <template #cell:members="{ row }">
            <div class="vorg__stack vorg__stack--center">
              <APill v-if="!row.master_count" kind="err">Master 없음</APill>
              <span v-else class="vorg__mono">Master {{ row.master_count }}</span>
              <span class="vorg__sub">강사 {{ row.instructor_count ?? 0 }} · 지정 {{ row.active_assignment_count ?? 0 }}</span>
            </div>
          </template>
          <template #cell:badges="{ row }">
            <div class="vorg__badges">
              <APill
                v-if="isPositive(row.balance_shortfall)"
                kind="err"
                :title="`부족 예상액 ${money(row.balance_shortfall, row.voucher_currency)}`"
              >잔액 부족 예상</APill>
              <APill v-if="row.contract_expired" kind="err" :title="`계약 종료 ${row.contract_end || ''}`">계약 만료</APill>
              <APill v-else-if="row.contract_ending_soon" kind="warn" :title="`계약 종료 ${row.contract_end || ''}`">계약 종료 임박</APill>
              <APill v-if="row.requested_purchase_count" kind="info">구매 요청 {{ row.requested_purchase_count }}</APill>
            </div>
          </template>
          <template #cell:actions="{ row }">
            <ABtn size="sm" kind="ghost" icon="chevron_right" icon-only title="상세" @click="$router.push(detailRoute(row))" />
          </template>
        </ATable>
        <APagination :current-page="page" :total="total" :page-size="size" @change="onPage" />
      </template>
    </ACard>

    <OrganizationFormModal v-model="formOpen" @saved="onCreated" />
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import LoadError from "@/views/voucher/components/LoadError.vue";
import OrganizationFormModal from "@/views/voucher/components/OrganizationFormModal.vue";
import { fmtKst, money, percent, isPositive, pageOf, bodyOf, loadErrorText, loadCurrencyCodes } from "@/utils/voucher";

const blankFilters = () => ({ q: "", status: "", currency: "", balance_filter: "" });

export default {
  name: "VoucherOrganizations",
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AEmpty, APagination, OrganizationFormModal, LoadError },
  data() {
    return {
      overview: { byCurrency: [], usdEquivalent: null, requestedCount: 0, requestedByCurrency: [], shortCount: 0, asOf: null },
      filters: blankFilters(),
      currencyFilterOptions: [],
      items: [],
      total: 0,
      page: 1,
      size: 20,
      loading: false,
      loadError: "",
      formOpen: false,
      columns: [
        { key: "name", label: "기관", width: 240 },
        { key: "status", label: "상태", width: 90, align: "center" },
        { key: "currency", label: "통화 · 할인", width: 110 },
        { key: "balance", label: "잔액", width: 170, align: "right" },
        { key: "members", label: "멤버", width: 120, align: "center" },
        { key: "badges", label: "확인할 것", wrap: true },
        { key: "actions", label: "", width: 56, align: "center" },
      ],
    };
  },
  async created() {
    this.reloadAll();
    this.currencyFilterOptions = await loadCurrencyCodes(this.$axios);
  },
  methods: {
    fmtKst,
    money,
    percent,
    isPositive,
    detailRoute(row) {
      return { name: "VoucherOrganizationDetail", params: { organizationIdx: row.organization_idx ?? row.idx } };
    },
    reloadAll() {
      this.loadOverview();
      this.load();
    },
    async loadOverview() {
      try {
        const res = await this.$axios({ method: "get", url: "/voucher/admin/balance/overview", loadingBar: false });
        const b = bodyOf(res) || {};
        this.overview = {
          byCurrency: (b.by_currency || []).map((c) => ({
            currency: c.currency_code || "USD",
            orgCount: Number(c.organization_count) || 0,
            balance: c.balance_total,
            monthPurchase: c.month_purchased,
            monthUse: c.month_registered,
          })),
          usdEquivalent: b.usd_equivalent_total ?? null,
          requestedCount: Number(b.requested_purchase_count) || 0,
          requestedByCurrency: b.requested_purchases || [],
          shortCount: Number(b.balance_short_organization_count) || 0,
          asOf: b.as_of || null,
        };
      } catch (e) {
        console.error("바우처 요약 조회 실패:", e);
      }
    },
    search() {
      this.page = 1;
      this.load();
    },
    resetFilters() {
      this.filters = blankFilters();
      this.search();
    },
    filterShort() {
      this.filters = { ...blankFilters(), balance_filter: "short" };
      this.search();
    },
    onPage(p) {
      this.page = p;
      this.load();
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const params = { page: this.page, size: this.size };
        Object.entries(this.filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const res = await this.$axios({ method: "get", url: "/voucher/admin/organizations", params, loadingBar: true });
        const p = pageOf(res, this.page);
        this.items = p.items;
        this.total = p.total;
      } catch (e) {
        console.error("기관 목록 조회 실패:", e);
        this.loadError = loadErrorText(e);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    onCreated(created) {
      const idx = created?.organization_idx ?? created?.idx;
      // 등록 직후 Master 가 없어 기관이 동작하지 않으므로, 상세로 보내 멤버·Master 지정을 바로 이어가게 합니다.
      if (idx) this.$router.push({ name: "VoucherOrganizationDetail", params: { organizationIdx: idx } });
      else this.reloadAll();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.vorg {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }

  &__stat {
    padding: 16px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-lg;
    text-align: left;

    &--btn {
      cursor: pointer;
      transition: border-color $p-dur $p-ease;

      &:hover { border-color: $p-line-hard; }
    }
  }

  &__stat-sub {
    margin-top: 4px;
    font-size: 11px;
    color: $p-ink-soft;

    &--err { color: $p-err-ink; font-weight: 700; }
  }

  &__filters {
    display: grid;
    grid-template-columns: repeat(5, 1fr) auto;
    gap: 12px;
    align-items: end;

    @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__filter--wide {
    grid-column: span 2;

    @media (max-width: 640px) { grid-column: span 1; }
  }

  &__filter-actions {
    display: flex;
    gap: 6px;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 48px;
  }

  &__stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    &--end { align-items: flex-end; }
    &--center { align-items: center; }
  }

  &__sub {
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__mono {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
  }

  &__link {
    color: $p-info-ink;
    font-weight: 700;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
}
</style>
